import * as React from "react";
import Paper from "@mui/material/Paper";
import { Button, Box, CircularProgress, IconButton } from "@mui/material";
import { Check, X } from "lucide-react";

// Dummy API endpoints - Replace these with actual API paths when ready
const API_ENDPOINTS = {
  SHOW_TABLE: "/api/clicks/gettype2fetchlinks",
  UPDATE_RECORDS: "/api/clicks/refreshtype2linkfetch",
  REFRESH_DATA: "/api/clicks/gett2furl",
  UPDATE_ROW: "/api/clicks/updatetype2linkdata",
};

const showTableAPI = async (selectedIds = []) => {
  try {
    const response = await fetch(API_ENDPOINTS.SHOW_TABLE, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // body: JSON.stringify({ selectedIds }),
    });

    if (!response.ok) {
      throw new Error("API call failed");
    }

    const data = await response.json();
    // console.log(data, "data from api");
    return data;
  } catch (error) {
    console.log("showTable API call failed, using dummy data:", error.message);
  }
};

const updateAPI = async (selectedIds) => {
  try {
    const response = await fetch(API_ENDPOINTS.UPDATE_RECORDS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ camp_url_id_arr: selectedIds }),
    });

    if (!response.ok) {
      throw new Error("Update API call failed");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.log("Update API call failed, using dummy response:", error.message);

    return {
      message: "Records updated successfully",
      success: true,
      updatedIds: selectedIds,
    };
  }
};

const refreshAPI = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.REFRESH_DATA, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Refresh API call failed");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.log(
      "Refresh API call failed, using dummy response:",
      error.message
    );

    return {
      message: "Data refreshed successfully",
      success: true,
    };
  }
};

const updateRowAPI = async (rowData) => {
  try {
    const response = await fetch(API_ENDPOINTS.UPDATE_ROW, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rowData),
    });

    if (!response.ok) {
      throw new Error("Update row API call failed");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.log(
      "Update row API call failed, using dummy response:",
      error.message
    );

    return {
      message: "Row updated successfully",
      success: true,
      updatedRow: rowData,
    };
  }
};

const columns = [
  {
    field: "camp_url_id",
    headerName: "Camp Url ID",
    width: 30,
    editable: false,
    sortable: true,
  },
  { field: "link", headerName: "Link", width: 250, editable: true },
  {
    field: "link2",
    headerName: "Link2",
    width: 350,
    editable: true,
    sortable: true,
  },
  {
    field: "new_link2",
    headerName: "New Link2",
    width: 350,
    editable: true,
    sortable: true,
  },
  {
    field: "url_match",
    headerName: "Url Match",
    width: 200,
    editable: false,
    sortable: true,
  },
  {
    field: "updt_time",
    headerName: "updated Time",
    description: "This column has a value getter and is not sortable.",
    sortable: true,
    width: 200,
    editable: false,
    valueGetter: (value) => {
      if (!value) return "";
      try {
        const date = new Date(value);
        const istDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const day = String(istDate.getDate()).padStart(2, "0");
        const month = String(istDate.getMonth() + 1).padStart(2, "0");
        const year = String(istDate.getFullYear()).slice(-2);
        const hours = String(istDate.getHours()).padStart(2, "0");
        const minutes = String(istDate.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
      } catch (e) {
        return value;
      }
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 100,
    editable: false,
  },
];

const paginationModel = { page: 0, pageSize: 5000 };

// Compares new_link2 against the campaign's url_match pattern (substring, case-insensitive)
const getMatchStatus = (row) => {
  if (!row.new_link2 || !row.url_match) return "none";
  return String(row.new_link2)
    .toLowerCase()
    .includes(String(row.url_match).toLowerCase())
    ? "match"
    : "mismatch";
};

export default function LinkFetcher() {
  const [rows, setRows] = React.useState([]);
  const [originalRows, setOriginalRows] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [updatingRows, setUpdatingRows] = React.useState(new Set());

  // Function to call showTable API
  const showTable = async (ids = []) => {
    console.log("Calling showTable API with selectedIds:", ids);
    setLoading(true);

    try {
      const response = await showTableAPI();

      if (response && response.success && response.data) {
        setRows(response.data);
        setOriginalRows(response.data.map((row) => ({ ...row })));
        console.log(
          "Table data loaded successfully:",
          response.data.length,
          "records"
        );
      } else if (
        !response.data ||
        !Array.isArray(response.data) ||
        response.data.length === 0
      ) {
        setRows([]);
        setOriginalRows([]);
        // throw new Error("Invalid response structure");
      } else {
        throw new Error("showTable API returned failure");
      }
    } catch (error) {
      console.error("Error in showTable function:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial data on component mount
  React.useEffect(() => {
    showTable();
  }, []);

  // Handle row selection change
  const handleSelectionModelChange = (newSelection) => {
    const stringIds = newSelection.map((id) => String(id));
    setSelectedIds(stringIds);
    console.log("Selected IDs updated:", stringIds);
  };

  // Handle update button click
  const handleUpdate = async () => {
    if (selectedIds.length === 0) {
      console.log("No records selected for update");
      return;
    }

    console.log("Starting update process for IDs:", selectedIds);
    setUpdating(true);

    try {
      const response = await updateAPI(selectedIds);

      if (response && response.success) {
        console.log("Update successful:", response.message);
        await showTable(selectedIds);
        setSelectedIds([]);
        console.log("Update completed successfully");
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Error in handleUpdate:", error);
    } finally {
      setUpdating(false);
    }
  };

  // Handle refresh button click
  const handleRefresh = async () => {
    console.log("Starting refresh process");
    setRefreshing(true);

    try {
      const response = await refreshAPI();

      if (response && response.success) {
        console.log("Refresh successful:", response.message);

        // Call showTable API after successful refresh
        await showTable();
        setSelectedIds([]);

        console.log("Table refreshed and data reloaded");
      } else {
        throw new Error("Refresh failed");
      }
    } catch (error) {
      console.error("Error in handleRefresh:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle cell value change
  const handleCellEdit = (id, field, value) => {
    // console.log(id, field, value);
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.camp_url_id === id ? { ...row, [field]: value } : row
      )
    );
  };

  // Check if row has been modified
  const isRowModified = (rowId) => {
    const currentRow = rows.find((row) => row.camp_url_id === rowId);
    const originalRow = originalRows.find((row) => row.camp_url_id === rowId);

    if (!currentRow || !originalRow) return false;

    return JSON.stringify(currentRow) !== JSON.stringify(originalRow);
  };

  // Handle row update (tick button)
  const handleRowUpdate = async (rowId) => {
    const rowData = rows.find((row) => row.camp_url_id === rowId);
    if (!rowData) return;

    // console.log("Updating row:", rowData);
    setUpdatingRows((prev) => new Set(prev).add(rowId));

    try {
      const response = await updateRowAPI(rowData);

      if (response && response.success) {
        console.log("Row update successful:", response.message);
        // Refresh the table data
        await showTable();
        console.log("Row updated and table refreshed");
      } else {
        throw new Error("Row update failed");
      }
    } catch (error) {
      console.error("Error in handleRowUpdate:", error);
    } finally {
      setUpdatingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  // Select rows whose new_link2 matches the campaign's url_match (green rows)
  const handleSelectMatched = () => {
    const matchedIds = rows
      .filter((row) => getMatchStatus(row) === "match")
      .map((row) => String(row.camp_url_id));
    setSelectedIds(matchedIds);
  };

  // Select rows whose new_link2 does not match the campaign's url_match (red rows)
  const handleSelectMismatched = () => {
    const mismatchedIds = rows
      .filter((row) => getMatchStatus(row) === "mismatch")
      .map((row) => String(row.camp_url_id));
    setSelectedIds(mismatchedIds);
  };

  // Select every row
  const handleSelectAllRows = () => {
    setSelectedIds(rows.map((row) => String(row.camp_url_id)));
  };

  // Handle row reset (cross button)
  const handleRowReset = (rowId) => {
    const originalRow = originalRows.find((row) => row.camp_url_id === rowId);
    if (!originalRow) return;

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.camp_url_id === rowId ? { ...originalRow } : row
      )
    );
    console.log("Row reset to original state:", originalRow);
  };

  return (
    <Box sx={{ width: "95%", mx: "auto", py: 3, overflow: "hidden" }}>
      {/* Button Controls */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdate}
          disabled={selectedIds.length === 0 || updating}
          startIcon={
            updating && <CircularProgress size={16} sx={{ color: "white" }} />
          }
        >
          {updating ? "Updating..." : "Update"}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={refreshing && <CircularProgress size={16} />}
        >
          {refreshing ? "Refreshing..." : "Refresh Table"}
        </Button>

        <Button
          variant="outlined"
          color="success"
          onClick={handleSelectMatched}
          disabled={rows.length === 0}
        >
          Select Matched (Green)
        </Button>

        <Button
          variant="outlined"
          color="error"
          onClick={handleSelectMismatched}
          disabled={rows.length === 0}
        >
          Select Mismatched (Red)
        </Button>

        <Button
          variant="outlined"
          onClick={handleSelectAllRows}
          disabled={rows.length === 0}
        >
          Select All
        </Button>
      </Box>

      {/* Data Table */}
      <Paper 
        elevation={0}
        sx={{ 
          width: "100%", 
          overflow: "hidden",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
        }}
      >
        <DataGrid
          rows={rows}
          originalRows={originalRows}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          rowSelectionModel={selectedIds.map((id) => Number(id))}
          onRowSelectionModelChange={handleSelectionModelChange}
          sx={{ border: 0 }}
          loading={loading}
          onCellEdit={handleCellEdit}
          isRowModified={isRowModified}
          onRowUpdate={handleRowUpdate}
          onRowReset={handleRowReset}
          updatingRows={updatingRows}
        />
      </Paper>
    </Box>
  );
}

// Enhanced Mock DataGrid with editing capabilities and proper overflow handling
const DataGrid = ({
  rows,
  originalRows,
  columns,
  initialState,
  pageSizeOptions,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
  sx,
  loading,
  onCellEdit,
  isRowModified,
  onRowUpdate,
  onRowReset,
  updatingRows,
}) => {
  const pageSize = initialState?.pagination?.paginationModel?.pageSize || 5;
  const [currentPage, setCurrentPage] = React.useState(0);

  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const currentRows = rows.slice(startIndex, endIndex);
  const totalPages = Math.ceil(rows.length / pageSize);

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = rows.map((row) => row.camp_url_id);
      onRowSelectionModelChange(allIds);
    } else {
      onRowSelectionModelChange([]);
    }
  };

  const handleRowSelect = (id, checked) => {
    if (checked) {
      onRowSelectionModelChange([...rowSelectionModel, id]);
    } else {
      onRowSelectionModelChange(
        rowSelectionModel.filter((selectedId) => selectedId !== id)
      );
    }
  };

  const handleCellChange = (rowId, field, value) => {
    // Convert age to number if it's the age field

    onCellEdit(rowId, field, value);
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              border: "2px solid #1976d2",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <span style={{ color: "#666" }}>Loading...</span>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const isAllSelected =
    rowSelectionModel.length === rows.length && rows.length > 0;
  const isPartiallySelected =
    rowSelectionModel.length > 0 && rowSelectionModel.length < rows.length;

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Table Container with proper overflow handling */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          maxWidth: "100%",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "700px", // Minimum width to prevent cramping
          }}
        >
          <thead
            style={{
              backgroundColor: "#fafafa",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <tr>
              {checkboxSelection && (
                <th
                  style={{
                    width: "52px",
                    padding: "16px 8px",
                    textAlign: "center",
                    borderBottom: "2px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      accentColor: "#1976d2",
                      cursor: "pointer",
                    }}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.field}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#334155",
                    borderBottom: "2px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                    whiteSpace: "nowrap",
                    textWrap: "wrap",
                  }}
                >
                  {col.headerName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, index) => {
              const isModified = isRowModified(row.camp_url_id);
              const isUpdating = updatingRows.has(row.camp_url_id);
              const matchStatus = getMatchStatus(row);

              const defaultBg = index % 2 === 0 ? "#ffffff" : "#f8fafc";
              const rowBg = isModified
                ? "#fff8f0"
                : matchStatus === "match"
                ? "#e8f5e9"
                : matchStatus === "mismatch"
                ? "#fdecea"
                : defaultBg;
              const rowBorder = isModified
                ? "4px solid #f97316"
                : matchStatus === "match"
                ? "4px solid #4caf50"
                : matchStatus === "mismatch"
                ? "4px solid #f44336"
                : "4px solid transparent";

              return (
                <tr
                  key={row.camp_url_id}
                  style={{
                    backgroundColor: rowBg,
                    borderBottom: "1px solid #f1f5f9",
                    borderLeft: rowBorder,
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    if (!isModified) {
                      e.currentTarget.style.backgroundColor = "#f1f5f9";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isModified) {
                      e.currentTarget.style.backgroundColor = rowBg;
                    }
                  }}
                >
                  {checkboxSelection && (
                    <td style={{ padding: "16px 8px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={rowSelectionModel.includes(row.camp_url_id)}
                        onChange={(e) =>
                          handleRowSelect(row.camp_url_id, e.target.checked)
                        }
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#1976d2",
                        }}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.field}
                      style={{
                        padding: "8px 16px",
                        fontSize: "0.875rem",
                        color: "#212121",
                        minWidth: col.width,
                        maxWidth: col.width,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: col.editable ? "normal" : "nowrap",
                        position: "relative",
                      }}
                    >
                      {col.field === "actions" ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => onRowUpdate(row.camp_url_id)}
                            disabled={!isModified || isUpdating}
                            style={{
                              padding: "4px",
                              backgroundColor:
                                isModified && !isUpdating
                                  ? "#4caf50"
                                  : "#e0e0e0",
                              color:
                                isModified && !isUpdating ? "white" : "#999",
                              opacity: isModified && !isUpdating ? 1 : 0.5,
                              cursor:
                                isModified && !isUpdating
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                          >
                            {isUpdating ? (
                              <div
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  border: "2px solid #fff",
                                  borderTop: "2px solid transparent",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                }}
                              ></div>
                            ) : (
                              <Check size={12} />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onRowReset(row.camp_url_id)}
                            disabled={!isModified || isUpdating}
                            style={{
                              padding: "4px",
                              backgroundColor:
                                isModified && !isUpdating
                                  ? "#f44336"
                                  : "#e0e0e0",
                              color:
                                isModified && !isUpdating ? "white" : "#999",
                              opacity: isModified && !isUpdating ? 1 : 0.5,
                              cursor:
                                isModified && !isUpdating
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                          >
                            <X size={12} />
                          </IconButton>
                        </div>
                      ) : col.editable ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <textarea
                            type={"text"}
                            value={row[col.field] || ""}
                            onChange={(e) =>
                              handleCellChange(
                                row.camp_url_id,
                                col.field,
                                e.target.value
                              )
                            }
                            style={{
                              flex: 1,
                              border: "none",
                              outline: "none",
                              backgroundColor: "transparent",
                              fontSize: "0.875rem",
                              padding: "4px 0",
                              color: "#212121",
                              resize: "none",
                              height: "auto",
                              overflow: "hidden",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                              textOverflow: "clip",
                              lineHeight: "1.4",
                            }}
                            onInput={(e) => {
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                            ref={(textarea) => {
                              if (textarea) {
                                textarea.style.height = "auto";
                                textarea.style.height = textarea.scrollHeight + "px";
                              }
                            }}
                          />
                          {(col.field === 'link' && row[col.field] && String(row[col.field]).startsWith('http')) && (
                            <IconButton 
                              size="small" 
                              component="a" 
                              href={row[col.field]} 
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: '4px', padding: '4px' }}
                              title="Open link in new tab"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </IconButton>
                          )}
                        </div>
                      ) : col.valueGetter ? (
                        col.valueGetter(row[col.field], row)
                      ) : (
                        row[col.field]
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          minHeight: "52px",
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: "0.875rem", color: "#616161" }}>
          {startIndex + 1}–{Math.min(endIndex, rows.length)} of {rows.length}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            style={{
              width: "32px",
              height: "32px",
              border: "none",
              backgroundColor: "transparent",
              cursor: currentPage === 0 ? "not-allowed" : "pointer",
              opacity: currentPage === 0 ? 0.5 : 1,
              borderRadius: "4px",
            }}
          >
            ‹
          </button>
          <span
            style={{ fontSize: "0.875rem", color: "#616161", margin: "0 8px" }}
          >
            {currentPage + 1} / {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
            }
            disabled={currentPage === totalPages - 1}
            style={{
              width: "32px",
              height: "32px",
              border: "none",
              backgroundColor: "transparent",
              cursor:
                currentPage === totalPages - 1 ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages - 1 ? 0.5 : 1,
              borderRadius: "4px",
            }}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};
